import type { V1AutomationInputSpec } from '@/generated/hooks/grimoire/models';
import { isNoneType, unwrapType } from './typeDetection';

/**
 * Checks if all required input specs have default values.
 *
 * For scheduling automations, we only check if required inputs are marked as defaults.
 * We don't validate the actual values since those will be provided at runtime.
 *
 * An input is considered "provided" if it is either:
 * 1. Optional (not required), OR
 * 2. Required AND marked as a default input
 *
 * @param inputSpecs - Array of automation input specifications
 * @param defaultInputKeys - Array of input names marked as defaults
 * @returns true if all required inputs are marked as defaults, false otherwise
 */
export function areAllRequiredInputsProvided(
  inputSpecs: V1AutomationInputSpec[],
  defaultInputKeys: string[],
): boolean {
  // If no input specs, all required inputs are satisfied (vacuously true)
  if (!inputSpecs || inputSpecs.length === 0) {
    return true;
  }

  // If defaultInputKeys is not provided, treat as empty array
  const defaults = defaultInputKeys || [];

  // Check each input spec
  for (const spec of inputSpecs) {
    const { isOptional } = unwrapType(spec.type);

    // Optional inputs don't block schedule
    if (isOptional) {
      continue;
    }

    // Nor does a `none_type` input: there is no value to mark as a default.
    if (isNoneType(spec.type)) {
      continue;
    }

    // Required input - check if it's marked as a default
    if (!defaults.includes(spec.name)) {
      return false;
    }
  }

  return true;
}
